/* =============================================
   OLFACTUM — Global JS (Alpine.js store + utils)
   ============================================= */

// ─── Utility: Format Money ────────────────────────────────────────────────
window.OlfactumUtils = {
  formatMoney(cents) {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }
};

document.addEventListener('alpine:init', () => {

  // ─── Cart Store ───────────────────────────────────────────────────────────
  Alpine.store('cart', {
    open: false,
    loading: false,
    items: [],
    totalQuantity: 0,
    totalPrice: 0,
    freeShippingThreshold: (() => {
      const raw = parseInt(document.documentElement.dataset.freeShipping);
      return isNaN(raw) ? 149900 : raw * 100;
    })(),

    async init() {
      Alpine.effect(() => {
        document.body.style.overflow = this.open ? 'hidden' : '';
      });
      await this.fetch();
    },

    async fetch() {
      try {
        const res = await fetch('/cart.js');
        if (!res.ok) throw new Error(res.statusText);
        const data = await res.json();
        this.items = data.items;
        this.totalQuantity = data.item_count;
        this.totalPrice = data.total_price;
      } catch(e) {
        console.error('Cart fetch error:', e);
      }
    },

    async addItem(variantId, quantity = 1, properties = {}) {
      this.loading = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity, properties }]
          })
        });
        if (!res.ok) throw new Error('Add to cart failed');
        await this.fetch();
        this.open = true;
      } catch(e) {
        console.error('Add to cart error:', e);
        Alpine.store('toast').show('Hubo un error al agregar el producto. Intenta de nuevo.');
      } finally {
        this.loading = false;
      }
    },

    async updateItem(key, quantity) {
      const MAX_QTY = 10;
      if (quantity > MAX_QTY) {
        Alpine.store('toast').show(`Máximo ${MAX_QTY} unidades por producto.`);
        return;
      }

      // Optimistic UI: save snapshot, update locally
      const prevItems = [...this.items.map(i => ({...i}))];
      const prevQty = this.totalQuantity;
      const prevPrice = this.totalPrice;

      const idx = this.items.findIndex(i => i.key === key);
      if (idx !== -1) {
        if (quantity === 0) {
          const removed = this.items[idx];
          this.items = this.items.filter(i => i.key !== key);
          this.totalQuantity = prevQty - removed.quantity;
          this.totalPrice = prevPrice - removed.final_line_price;
        } else {
          const diff = quantity - this.items[idx].quantity;
          const unitPrice = this.items[idx].final_line_price / this.items[idx].quantity;
          this.items[idx] = {...this.items[idx], quantity, final_line_price: Math.round(unitPrice * quantity)};
          this.items = [...this.items];
          this.totalQuantity = prevQty + diff;
          this.totalPrice = prevPrice + Math.round(unitPrice * diff);
        }
      }

      this.loading = true;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Error al actualizar el carrito');
        }
        const data = await res.json();
        if (typeof data.item_count !== 'undefined') {
          this.items = data.items;
          this.totalQuantity = data.item_count;
          this.totalPrice = data.total_price;
        }
      } catch(e) {
        console.error('Update cart error:', e);
        // Rollback optimistic update
        this.items = prevItems;
        this.totalQuantity = prevQty;
        this.totalPrice = prevPrice;
        Alpine.store('toast').show(e.message || 'No se pudo actualizar la cantidad. Intenta de nuevo.');
      } finally {
        this.loading = false;
      }
    },

    async removeItem(key) {
      await this.updateItem(key, 0);
    },

    get remainingForFreeShipping() {
      return Math.max(0, this.freeShippingThreshold - this.totalPrice);
    },

    get freeShippingProgress() {
      if (this.freeShippingThreshold <= 0) return 100;
      return Math.min(100, (this.totalPrice / this.freeShippingThreshold) * 100);
    },

    get hasFreeShipping() {
      return this.totalPrice >= this.freeShippingThreshold;
    },

    formatMoney(cents) {
      return OlfactumUtils.formatMoney(cents);
    }
  });

  // ─── Toast Notifications ────────────────────────────────────────────────
  Alpine.store('toast', {
    message: '',
    visible: false,
    _timeout: null,

    show(msg, duration = 3000) {
      clearTimeout(this._timeout);
      this.message = msg;
      this.visible = true;
      this._timeout = setTimeout(() => { this.visible = false; }, duration);
    }
  });

  // ─── Mobile Menu ──────────────────────────────────────────────────────────
  Alpine.store('mobileMenu', {
    open: false,
    toggle() { this.open = !this.open; },
    close() { this.open = false; }
  });

  // ─── Account Modal ─────────────────────────────────────────────────────────
  Alpine.store('accountModal', {
    open: false,
    view: 'login',   // 'login' | 'register' | 'recover'
    close() { this.open = false; },
    show(view = 'login') { this.view = view; this.open = true; }
  });

  // ─── Search Overlay ────────────────────────────────────────────────────────
  Alpine.store('search', {
    isOpen: false,
    query: '',
    results: [],
    loading: false,
    activeIndex: -1,
    _debounceTimer: null,

    open() {
      this.isOpen = true;
      this.query = '';
      this.results = [];
      this.activeIndex = -1;
      setTimeout(() => {
        const input = document.getElementById('search-input');
        if (input) input.focus();
      }, 50);
    },

    close() {
      this.isOpen = false;
      this.results = [];
      this.activeIndex = -1;
    },

    onInput() {
      clearTimeout(this._debounceTimer);
      const q = this.query.trim();
      if (q.length < 2) {
        this.results = [];
        this.activeIndex = -1;
        return;
      }
      this.loading = true;
      this._debounceTimer = setTimeout(() => this._fetch(q), 300);
    },

    _fetch(q) {
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6')
        .then(function(r) { return r.json(); })
        .then(function(data) {
          var store = Alpine.store('search');
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          store.results = products.map(function(p) {
            return {
              title: p.title,
              url: p.url,
              vendor: p.vendor || '',
              price: p.price,
              compareAtPrice: p.compare_at_price,
              image: p.image || (p.featured_image && p.featured_image.url) || ''
            };
          });
          store.loading = false;
          store.activeIndex = -1;
        })
        .catch(function() {
          var store = Alpine.store('search');
          store.results = [];
          store.loading = false;
        });
    },

    onKeydown(event) {
      if (!this.results.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = (this.activeIndex + 1) % this.results.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = this.activeIndex <= 0 ? this.results.length - 1 : this.activeIndex - 1;
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        window.location.href = this.results[this.activeIndex].url;
      }
    },

    goTo(url) {
      window.location.href = url;
    },

    formatPrice(price) {
      if (!price) return '';
      var cents = typeof price === 'string' ? parseFloat(price) * 100 : price;
      return '$' + (cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },

    submit() {
      if (this.query.trim()) {
        window.location.href = '/search?q=' + encodeURIComponent(this.query.trim()) + '&type=product';
      }
    }
  });

  // ─── Wishlist Store ────────────────────────────────────────────────────────
  Alpine.store('wishlist', {
    items: [],
    _syncTid: null,

    init() {
      try {
        const bootstrap = window.OLFACTUM_BOOTSTRAP && window.OLFACTUM_BOOTSTRAP.wishlist;
        const saved = localStorage.getItem('olfactum_wishlist');
        const local = saved ? JSON.parse(saved) : [];
        // Bootstrap (server-side metafield) es la fuente de verdad cuando hay sesión.
        // localStorage queda como caché para respuesta instantánea sin recargar.
        this.items = Array.isArray(bootstrap) ? bootstrap : local;
        this._save();
      } catch(e) {
        console.error('Wishlist load error:', e);
        this.items = [];
      }
    },

    _save() {
      try {
        localStorage.setItem('olfactum_wishlist', JSON.stringify(this.items));
      } catch(e) {
        console.error('Wishlist save error:', e);
      }
    },

    _syncRemote() {
      const auth = window.OLFACTUM_AUTH;
      if (!auth || document.body.dataset.customerLoggedIn !== 'true') return;
      const endpoint = window.OLFACTUM_WISHLIST_ENDPOINT;
      if (!endpoint) return;
      // Debounce: agrupar toggles rápidos en un solo POST
      clearTimeout(this._syncTid);
      this._syncTid = setTimeout(() => {
        try {
          fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ auth, items: this.items }),
            keepalive: true
          }).catch(() => { /* caché local ya está actualizado */ });
        } catch(e) { /* noop */ }
      }, 400);
    },

    has(productId) {
      return this.items.some(item => item.id === productId);
    },

    toggle(productId, productData) {
      const isLoggedIn = document.body.dataset.customerLoggedIn === 'true';
      if (!isLoggedIn) {
        Alpine.store('toast').show('Inicia sesión para guardar tus favoritos ♡', 4000);
        Alpine.store('accountModal').view = 'login';
        Alpine.store('accountModal').open = true;
        return;
      }
      if (this.has(productId)) {
        this.remove(productId);
      } else {
        this.items.push({
          id: productData.id,
          handle: productData.handle,
          title: productData.title,
          price: productData.price,
          image: productData.image,
          url: productData.url
        });
        this._save();
        this._syncRemote();
      }
    },

    remove(productId) {
      this.items = this.items.filter(item => item.id !== productId);
      this._save();
      this._syncRemote();
    },

    get count() {
      return this.items.length;
    }
  });

  // ─── Cookie Consent Component ──────────────────────────────────────────────
  Alpine.data('cookieBanner', () => ({
    visible: false,

    init() {
      const consent = localStorage.getItem('olfactum_cookie_consent');
      if (consent === 'accepted') {
        this.loadAnalytics();
      } else if (consent !== 'rejected') {
        this.visible = true;
      }
    },

    accept() {
      localStorage.setItem('olfactum_cookie_consent', 'accepted');
      this.visible = false;
      this.loadAnalytics();
    },

    reject() {
      localStorage.setItem('olfactum_cookie_consent', 'rejected');
      this.visible = false;
    },

    loadAnalytics() {
      const cfg = window.__olfactum || {};

      // GA4
      const ga4Id = cfg.ga4Id;
      if (ga4Id) {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + ga4Id;
        document.head.appendChild(script);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { dataLayer.push(arguments); };
        gtag('js', new Date());
        gtag('config', ga4Id, { currency: 'MXN', country: 'MX' });
      }

      // Facebook Pixel — ID read from window.__olfactum (not DOM dataset)
      const fbId = cfg.fbPixelId;
      if (fbId) {
        !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', fbId);
        fbq('track', 'PageView');
        window.dispatchEvent(new CustomEvent('fbq:ready'));
      }
    }
  }));

  // ─── Product Card Component ───────────────────────────────────────────────
  Alpine.data('productCard', (el) => ({
    selectedVariantId: parseInt(el.dataset.variantId) || 0,
    selectedPrice: parseInt(el.dataset.price) || 0,
    selectedComparePrice: parseInt(el.dataset.comparePrice) || 0,
    selectedVariantTitle: el.dataset.variantTitle || '',
    variants: (() => {
      try {
        return JSON.parse(el.dataset.variants || '[]');
      } catch(e) {
        console.error('Failed to parse variants:', e);
        return [];
      }
    })(),

    get isAvailable() {
      const v = this.variants.find(v => v.id === this.selectedVariantId);
      return v ? v.available : false;
    },

    selectVariantBySize(size) {
      // Busca por option1 (presentación: "Decant 5ml", "Decant 10ml", "Botella Completa")
      const match = this.variants.find(v => {
        const opt1 = (v.option1 || '').trim();
        const sizeStr = (size || '').trim();
        return opt1 === sizeStr || v.title === sizeStr;
      });

      if (match) {
        this.selectedVariantId    = match.id;
        this.selectedPrice        = parseInt(match.price) || 0;
        this.selectedComparePrice = parseInt(match.compare_at_price || 0) || 0;
        this.selectedVariantTitle = match.option1 || size;
      }
    },

    init() {
      // Preload secondary image when card enters viewport so hover is instant
      const img = this.$el.querySelector('[data-secondary-src]');
      if (!img) return;
      const observer = new IntersectionObserver((entries, obs) => {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          img.onload = () => img.classList.add('is-loaded');
          img.src = img.dataset.secondarySrc;
        }
      }, { rootMargin: '200px' });
      observer.observe(this.$el);
    },

    formatPrice(cents) {
      return OlfactumUtils.formatMoney(cents);
    },

    loadSecondaryImage() {
      // Fallback: trigger manually if IntersectionObserver didn't fire yet
      const img = this.$el.querySelector('[data-secondary-src]');
      if (!img || img.src) return;
      img.onload = () => img.classList.add('is-loaded');
      img.src = img.dataset.secondarySrc;
    },

    async addToCart() {
      await Alpine.store('cart').addItem(this.selectedVariantId, 1);
      if (typeof window.fbq === 'function') {
        fbq('track', 'AddToCart', {
          content_ids: [String(this.selectedVariantId)],
          content_type: 'product',
          value: this.selectedPrice / 100,
          currency: 'MXN'
        });
      }
    }
  }));

});
